/**
 * 📦 数组数据管理器
 *
 * 负责管理数组模式的RSM车辆数据
 * 支持场景播放、帧控制和数据检索
 */

import { RSMMessage, VehicleData, RSMDataAdapter } from './RSMDataAdapter';

// === 数据结构定义 ===

/**
 * RSM数据帧
 */
export interface RSMFrame {
  /** 帧序号 (从0开始) */
  frame_id: number;
  /** 时间戳 (相对开始时间的秒数) */
  timestamp: number;
  /** 该帧的RSM消息列表 */
  rsmMessages: RSMMessage[];
}

/**
 * 场景信息
 */
export interface ScenarioInfo {
  /** 场景ID */
  id: string;
  /** 场景名称 */
  name: string;
  /** 场景描述 */
  description?: string;
  /** 场景时长 (秒) */
  duration: number;
  /** 帧率 (fps) */
  frame_rate: number;
}

/**
 * 播放配置
 */
export interface PlaybackConfig {
  /** 是否自动播放 */
  auto_play: boolean;
  /** 是否循环播放 */
  loop: boolean;
  /** 默认播放速度倍数 */
  default_speed: number;
}

/**
 * 数组数据源
 */
export interface ArrayDataSource {
  /** 数据源类型 */
  type: 'array';
  /** 场景信息 */
  scenario: ScenarioInfo;
  /** 时间帧数据 */
  frames: RSMFrame[];
  /** 播放配置 */
  playback_config?: PlaybackConfig;
}

/**
 * 数据统计信息
 */
export interface DataStatistics {
  /** 总帧数 */
  totalFrames: number;
  /** 总时长（秒） */
  totalDuration: number;
  /** 帧率 */
  frameRate: number;
  /** 车辆数量统计 */
  vehicleCount: {
    /** 最小车辆数 */
    min: number;
    /** 最大车辆数 */
    max: number;
    /** 平均车辆数 */
    average: number;
  };
  /** 数据大小（字节） */
  dataSize: number;
}

// === 事件类型定义 ===

/**
 * 数组数据管理器事件
 */
export interface ArrayDataManagerEvents {
  /** 数据加载完成 */
  'data:loaded': (source: ArrayDataSource, statistics: DataStatistics) => void;
  /** 数据加载错误 */
  'data:error': (error: Error) => void;
  /** 帧变化 */
  'frame:change': (
    frame: RSMFrame,
    vehicles: VehicleData[],
    frameIndex: number
  ) => void;
  /** 播放状态变化 */
  'playback:statusChange': (isPlaying: boolean, frameIndex: number) => void;
}

// === 数组数据管理器类 ===

/**
 * 数组数据管理器
 */
export class ArrayDataManager {
  private dataSource: ArrayDataSource | null = null;
  private currentFrameIndex: number = 0;
  private eventListeners: Map<string, Set<Function>> = new Map();
  private statistics: DataStatistics | null = null;

  constructor() {
    this.initializeEventListeners();
  }

  /**
   * 初始化事件监听器
   */
  private initializeEventListeners(): void {
    const events: Array<keyof ArrayDataManagerEvents> = [
      'data:loaded',
      'data:error',
      'frame:change',
      'playback:statusChange',
    ];

    events.forEach(event => {
      this.eventListeners.set(event, new Set());
    });
  }

  /**
   * 加载数据源
   */
  async loadData(source: ArrayDataSource): Promise<void> {
    try {
      this.validateDataSource(source);

      this.dataSource = source;
      this.currentFrameIndex = 0;

      // 计算统计信息
      this.statistics = this.calculateStatistics(source);

      console.log(
        `[ArrayDataManager] 数据加载成功: ${source.scenario.name}, 总帧数: ${source.frames.length}`
      );

      // 触发加载完成事件
      this.emit('data:loaded', source, this.statistics);
    } catch (error) {
      console.error('[ArrayDataManager] 数据加载失败:', error);
      this.emit('data:error', error as Error);
      throw error;
    }
  }

  /**
   * 加载JSON数据文件
   */
  async loadFromJSON(jsonData: any): Promise<void> {
    try {
      // 验证JSON数据格式
      if (!jsonData || typeof jsonData !== 'object') {
        throw new Error('无效的JSON数据格式');
      }

      if (jsonData.type !== 'array') {
        throw new Error(`不支持的数据类型: ${jsonData.type}`);
      }

      // 转换为ArrayDataSource格式
      const dataSource: ArrayDataSource = {
        type: 'array',
        scenario: {
          id: jsonData.scenario?.id || `scenario_${Date.now()}`,
          name: jsonData.scenario?.name || '未命名场景',
          description: jsonData.scenario?.description,
          duration: jsonData.scenario?.duration || 0,
          frame_rate: jsonData.scenario?.frame_rate || 10,
        },
        frames: jsonData.frames || [],
        playback_config: {
          auto_play: jsonData.playback_config?.auto_play ?? false,
          loop: jsonData.playback_config?.loop ?? true,
          default_speed: jsonData.playback_config?.default_speed ?? 1.0,
        },
      };

      await this.loadData(dataSource);
    } catch (error) {
      console.error('[ArrayDataManager] JSON数据加载失败:', error);
      throw error;
    }
  }

  /**
   * 获取当前帧
   */
  getCurrentFrame(): RSMFrame | null {
    if (
      !this.dataSource ||
      this.currentFrameIndex < 0 ||
      this.currentFrameIndex >= this.dataSource.frames.length
    ) {
      return null;
    }
    return this.dataSource.frames[this.currentFrameIndex];
  }

  /**
   * 获取指定索引的帧
   */
  getFrameAt(frameIndex: number): RSMFrame | null {
    if (
      !this.dataSource ||
      frameIndex < 0 ||
      frameIndex >= this.dataSource.frames.length
    ) {
      return null;
    }
    return this.dataSource.frames[frameIndex];
  }

  /**
   * 设置当前帧索引
   */
  setCurrentFrameIndex(frameIndex: number): boolean {
    if (
      !this.dataSource ||
      frameIndex < 0 ||
      frameIndex >= this.dataSource.frames.length
    ) {
      return false;
    }

    if (this.currentFrameIndex !== frameIndex) {
      this.currentFrameIndex = frameIndex;
      const frame = this.getCurrentFrame();
      if (frame) {
        const vehicleData = this.convertFrameToVehicleData(frame);
        this.emit('frame:change', frame, vehicleData, frameIndex);
      }
    }

    return true;
  }

  /**
   * 跳转到下一帧
   */
  nextFrame(): boolean {
    if (!this.dataSource) return false;

    const nextIndex = this.currentFrameIndex + 1;
    if (nextIndex < this.dataSource.frames.length) {
      return this.setCurrentFrameIndex(nextIndex);
    }

    // 如果启用循环播放，回到第一帧
    if (this.dataSource.playback_config?.loop) {
      return this.setCurrentFrameIndex(0);
    }

    return false;
  }

  /**
   * 跳转到上一帧
   */
  previousFrame(): boolean {
    if (!this.dataSource) return false;

    const prevIndex = this.currentFrameIndex - 1;
    if (prevIndex >= 0) {
      return this.setCurrentFrameIndex(prevIndex);
    }

    // 如果启用循环播放，跳到最后一帧
    if (this.dataSource.playback_config?.loop) {
      return this.setCurrentFrameIndex(this.dataSource.frames.length - 1);
    }

    return false;
  }

  /**
   * 跳转到指定时间
   */
  seekToTime(timeInSeconds: number): boolean {
    if (!this.dataSource) return false;

    // 根据时间计算帧索引
    const frameIndex = Math.floor(
      timeInSeconds * this.dataSource.scenario.frame_rate
    );
    return this.setCurrentFrameIndex(frameIndex);
  }

  /**
   * 跳转到指定进度（0-1）
   */
  seekToProgress(progress: number): boolean {
    if (!this.dataSource || progress < 0 || progress > 1) return false;

    const frameIndex = Math.floor(
      progress * (this.dataSource.frames.length - 1)
    );
    return this.setCurrentFrameIndex(frameIndex);
  }

  /**
   * 将帧数据转换为车辆数据
   */
  convertFrameToVehicleData(frame: RSMFrame): VehicleData[] {
    if (!frame || !frame.rsmMessages) {
      return [];
    }

    return RSMDataAdapter.convertRSMBatch(frame.rsmMessages);
  }

  /**
   * 获取总帧数
   */
  getTotalFrames(): number {
    return this.dataSource?.frames.length || 0;
  }

  /**
   * 获取总时长（秒）
   */
  getDuration(): number {
    return this.dataSource?.scenario.duration || 0;
  }

  /**
   * 获取帧率
   */
  getFrameRate(): number {
    return this.dataSource?.scenario.frame_rate || 0;
  }

  /**
   * 获取当前进度（0-1）
   */
  getProgress(): number {
    if (!this.dataSource || this.dataSource.frames.length === 0) {
      return 0;
    }
    return this.currentFrameIndex / (this.dataSource.frames.length - 1);
  }

  /**
   * 获取当前时间（秒）
   */
  getCurrentTime(): number {
    if (!this.dataSource) return 0;
    return this.currentFrameIndex / this.dataSource.scenario.frame_rate;
  }

  /**
   * 获取场景信息
   */
  getScenarioInfo(): ScenarioInfo | null {
    return this.dataSource?.scenario || null;
  }

  /**
   * 获取播放配置
   */
  getPlaybackConfig(): PlaybackConfig | null {
    return this.dataSource?.playback_config || null;
  }

  /**
   * 获取数据统计信息
   */
  getStatistics(): DataStatistics | null {
    return this.statistics;
  }

  /**
   * 获取指定时间范围的帧
   */
  getFramesInRange(startTime: number, endTime: number): RSMFrame[] {
    if (!this.dataSource) return [];

    const frameRate = this.dataSource.scenario.frame_rate;
    const startFrame = Math.floor(startTime * frameRate);
    const endFrame = Math.ceil(endTime * frameRate);

    return this.dataSource.frames.slice(
      Math.max(0, startFrame),
      Math.min(this.dataSource.frames.length, endFrame + 1)
    );
  }

  /**
   * 搜索包含特定车辆ID的帧
   */
  findFramesWithVehicle(vehicleId: string): RSMFrame[] {
    if (!this.dataSource) return [];

    return this.dataSource.frames.filter(frame => {
      return frame.rsmMessages.some(rsmMessage =>
        rsmMessage.participants.some(
          participant =>
            `${rsmMessage.rsuId}_${participant.ptcId}` === vehicleId
        )
      );
    });
  }

  /**
   * 获取所有出现过的车辆ID
   */
  getAllVehicleIds(): Set<string> {
    const vehicleIds = new Set<string>();

    if (!this.dataSource) return vehicleIds;

    this.dataSource.frames.forEach(frame => {
      frame.rsmMessages.forEach(rsmMessage => {
        rsmMessage.participants.forEach(participant => {
          const vehicleId = `${rsmMessage.rsuId}_${participant.ptcId}`;
          vehicleIds.add(vehicleId);
        });
      });
    });

    return vehicleIds;
  }

  /**
   * 验证数据源
   */
  private validateDataSource(source: ArrayDataSource): void {
    if (!source) {
      throw new Error('数据源不能为空');
    }

    if (source.type !== 'array') {
      throw new Error(`不支持的数据源类型: ${source.type}`);
    }

    if (!source.scenario) {
      throw new Error('场景信息缺失');
    }

    if (!source.scenario.id || !source.scenario.name) {
      throw new Error('场景ID和名称不能为空');
    }

    if (!Array.isArray(source.frames)) {
      throw new Error('帧数据必须是数组');
    }

    if (source.frames.length === 0) {
      throw new Error('帧数据不能为空');
    }

    // 验证帧数据格式
    source.frames.forEach((frame, index) => {
      if (typeof frame.frame_id !== 'number') {
        throw new Error(`帧 ${index} 的frame_id无效`);
      }

      if (typeof frame.timestamp !== 'number') {
        throw new Error(`帧 ${index} 的timestamp无效`);
      }

      if (!Array.isArray(frame.rsmMessages)) {
        throw new Error(`帧 ${index} 的rsmMessages必须是数组`);
      }
    });
  }

  /**
   * 计算数据统计信息
   */
  private calculateStatistics(source: ArrayDataSource): DataStatistics {
    let totalVehicleCount = 0;
    let minVehicles = Infinity;
    let maxVehicles = 0;

    source.frames.forEach(frame => {
      const frameVehicleCount = frame.rsmMessages.reduce(
        (count, rsmMessage) => {
          return count + rsmMessage.participants.length;
        },
        0
      );

      totalVehicleCount += frameVehicleCount;
      minVehicles = Math.min(minVehicles, frameVehicleCount);
      maxVehicles = Math.max(maxVehicles, frameVehicleCount);
    });

    const averageVehicles =
      source.frames.length > 0 ? totalVehicleCount / source.frames.length : 0;

    // 计算数据大小（粗略估算）
    const dataSize = JSON.stringify(source).length;

    return {
      totalFrames: source.frames.length,
      totalDuration: source.scenario.duration,
      frameRate: source.scenario.frame_rate,
      vehicleCount: {
        min: minVehicles === Infinity ? 0 : minVehicles,
        max: maxVehicles,
        average: Math.round(averageVehicles * 100) / 100,
      },
      dataSize,
    };
  }

  /**
   * 事件监听
   */
  on<K extends keyof ArrayDataManagerEvents>(
    event: K,
    handler: ArrayDataManagerEvents[K]
  ): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.add(handler);
    }
  }

  /**
   * 移除事件监听
   */
  off<K extends keyof ArrayDataManagerEvents>(
    event: K,
    handler: ArrayDataManagerEvents[K]
  ): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * 触发事件
   */
  private emit<K extends keyof ArrayDataManagerEvents>(
    event: K,
    ...args: Parameters<ArrayDataManagerEvents[K]>
  ): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          (handler as any)(...args);
        } catch (error) {
          console.error(`[ArrayDataManager] 事件处理器错误 (${event}):`, error);
        }
      });
    }
  }

  /**
   * 清理资源
   */
  dispose(): void {
    this.dataSource = null;
    this.currentFrameIndex = 0;
    this.eventListeners.clear();
  }

  /**
   * 重置到初始状态
   */
  reset(): void {
    this.currentFrameIndex = 0;
    if (this.dataSource) {
      const frame = this.getCurrentFrame();
      if (frame) {
        const vehicleData = this.convertFrameToVehicleData(frame);
        this.emit('frame:change', frame, vehicleData, 0);
      }
    }
  }
}

// === 导出工厂函数 ===

/**
 * 创建数组数据管理器
 */
export function createArrayDataManager(): ArrayDataManager {
  return new ArrayDataManager();
}

// === 默认导出 ===

export default ArrayDataManager;
